"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import { Link, useRouter } from "@/i18n/navigation";
import { useGameLocale, useT } from "@/lib/i18n";
import { cachedFetch } from "@/lib/fetch-cache";
import { fmtDateTimePacific } from "@/lib/pacific";
import CharacterTag, { characterName } from "@/app/components/CharacterTag";
import { ladderTitle, sortPlayers, type EloBoard, type SortKey } from "@/lib/elo-board";
import { PlayerBadge } from "@/app/components/SupporterBadge";
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
interface CharacterNameRow {
  id: string;
  name: string;
}
const TH = "py-2 px-2 sm:px-3 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider";
function rankTone(rank: number): string {
  if (rank === 1) return "text-[var(--accent-gold)] font-bold";
  if (rank <= 3) return "text-[var(--accent-gold)] font-semibold";
  return "text-[var(--text-muted)]";
}
export default function EloBoardClient() {
  const $ = _c(69);
  const t = useT();
  const lang = useGameLocale();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [board, setBoard] = useState(null);
  const [failed, setFailed] = useState(false);
  const [reloads, setReloads] = useState(0);
  const rawSort = searchParams.get("sort");
  const [sortKey, setSortKey] = useState(rawSort === "lifetime" ? "lifetime" : "elo");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [charNames, setCharNames] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      let alive = true;
      setFailed(false);
      cachedFetch(`${API}/api/leaderboards/elo`).then(data => {
        if (!alive) {
          return;
        }
        if (!data || !Array.isArray(data.players)) {
          throw new Error("bad board");
        }
        setBoard(data);
      }).catch(() => {
        if (alive) {
          setFailed(true);
        }
      });
      return () => {
        alive = false;
      };
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== reloads) {
    t2 = [reloads];
    $[2] = reloads;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] !== lang) {
    t3 = () => {
      let alive_0 = true;
      cachedFetch(`${API}/api/characters?lang=${lang}`).then(rows => {
        if (!alive_0 || !Array.isArray(rows)) {
          return;
        }
        const m = {};
        for (const c of rows) {
          m[c.id.toLowerCase()] = c.name;
        }
        setCharNames(m);
      }).catch(_temp);
      return () => {
        alive_0 = false;
      };
    };
    t4 = [lang];
    $[4] = lang;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== charNames) {
    t5 = id => charNames[id.toLowerCase()] ?? characterName(id);
    $[7] = charNames;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const name = t5;
  let t6;
  if ($[9] !== board || $[10] !== sortKey) {
    t6 = board?.players ? sortPlayers(board.players, sortKey) : [];
    $[9] = board;
    $[10] = sortKey;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const rows_0 = t6;
  let t7;
  if ($[12] !== router) {
    t7 = key => {
      setSortKey(key);
      router.replace(key === "elo" ? "/top-players" : "/top-players?sort=lifetime", {
        scroll: false
      });
    };
    $[12] = router;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const setSort = t7;
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="text-[var(--accent-gold)]">Spire Codex</span>;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t) {
    t9 = t("Top Players");
    $[15] = t;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t9) {
    t10 = <h1 className="text-3xl font-bold mb-2">{t8}{" "}<span className="text-[var(--text-primary)]">{t9}</span></h1>;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = board?.min_runs ?? 10;
  let t12;
  if ($[19] !== t || $[20] !== t11) {
    t12 = t("elo_intro", {
      n: t11
    });
    $[19] = t;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12) {
    t13 = <p className="text-sm text-[var(--text-muted)] mb-4 max-w-3xl">{t12}</p>;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== board || $[25] !== t) {
    t14 = board?.computed_at && <span>{t("Refreshed nightly. Last build: {when}", {
        when: fmtDateTimePacific(board.computed_at)
      })}</span>;
    $[24] = board;
    $[25] = t;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== board || $[28] !== t) {
    t15 = board && board.total_rated > 0 && <span>{t("{n} rated players", {
        n: board.total_rated.toLocaleString()
      })}</span>;
    $[27] = board;
    $[28] = t;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t) {
    t16 = t("Sort by");
    $[30] = t;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t16) {
    t17 = <span>{t16}</span>;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== setSort) {
    t18 = () => setSort("elo");
    $[34] = setSort;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  const t19 = `px-3 py-1 rounded-md text-sm border transition-colors ${sortKey === "elo" ? "border-[var(--accent-gold)] text-[var(--accent-gold)]" : "border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"}`;
  let t20;
  if ($[36] !== t) {
    t20 = t("Elo");
    $[36] = t;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t18 || $[39] !== t19 || $[40] !== t20) {
    t21 = <button type="button" onClick={t18} className={t19}>{t20}</button>;
    $[38] = t18;
    $[39] = t19;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== setSort) {
    t22 = () => setSort("lifetime");
    $[42] = setSort;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  const t23 = `px-3 py-1 rounded-md text-sm border transition-colors ${sortKey === "lifetime" ? "border-[var(--accent-gold)] text-[var(--accent-gold)]" : "border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"}`;
  let t24;
  if ($[44] !== t) {
    t24 = t("Lifetime");
    $[44] = t;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== t22 || $[47] !== t23 || $[48] !== t24) {
    t25 = <button type="button" onClick={t22} className={t23}>{t24}</button>;
    $[46] = t22;
    $[47] = t23;
    $[48] = t24;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== t17 || $[51] !== t21 || $[52] !== t25) {
    t26 = <span className="inline-flex items-center gap-2 ml-auto">{t17}{t21}{t25}</span>;
    $[50] = t17;
    $[51] = t21;
    $[52] = t25;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== t14 || $[55] !== t15 || $[56] !== t26) {
    t27 = <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-[var(--text-muted)] mb-4">{t14}{t15}{t26}</div>;
    $[54] = t14;
    $[55] = t15;
    $[56] = t26;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== board || $[59] !== failed || $[60] !== name || $[61] !== rows_0 || $[62] !== t) {
    t28 = <div className="bg-[var(--bg-card)] border border-[var(--border-subtle)] rounded-lg">{failed ? <p className="text-center py-8 text-[var(--text-muted)]">{t("Something went wrong")}{" "}<button type="button" onClick={() => setReloads(_temp2)} className="underline hover:text-[var(--text-primary)]">{t("Try again")}</button></p> : board && rows_0.length === 0 ? <p className="text-center py-8 text-[var(--text-muted)]">{t("No ratings yet. The board fills in after the next nightly build.")}</p> : !board ? <p className="text-center py-8 text-[var(--text-muted)]">{t("Loading...")}</p> : <table className="w-full text-sm"><thead><tr className="border-b border-[var(--border-subtle)]"><th className={`text-left ${TH}`}>{t("Rank")}</th><th className={`text-left ${TH}`}>{t("Player")}</th><th className={`text-right ${TH}`}>{t("Elo")}</th><th className={`hidden sm:table-cell text-right ${TH}`}>{t("Lifetime")}</th><th className={`hidden sm:table-cell text-right ${TH}`}>{t("A10 runs")}</th><th className={`hidden md:table-cell text-right ${TH}`}>{t("Wins")}</th><th className={`text-right ${TH}`}>{t("Win rate")}</th><th className={`text-left ${TH}`}>{t("Main")}</th></tr></thead><tbody>{rows_0.map(p => {
            const shown = p.rank;
            return <tr key={`${p.username}-${p.rank}`} className="border-b border-[var(--border-subtle)] last:border-0 hover:bg-[var(--bg-card-hover)]"><td className={`py-2 px-2 sm:px-3 tabular-nums ${rankTone(shown)}`}>{shown}</td><td className="py-2 px-2 sm:px-3 min-w-0"><Link href={`/players/${encodeURIComponent(p.username)}`} className="font-medium text-[var(--text-primary)] hover:text-[var(--accent-gold)] truncate block max-w-[9rem] sm:max-w-none">{p.username}<PlayerBadge username={p.username} className="ml-1" /></Link></td><td className="py-2 px-2 sm:px-3 text-right tabular-nums font-semibold text-[var(--text-primary)]">{Math.round(p.elo)}</td><td className="hidden sm:table-cell py-2 px-3 text-right tabular-nums text-[var(--text-secondary)]">{Math.round(p.lifetime)}</td><td className="hidden sm:table-cell py-2 px-3 text-right tabular-nums text-[var(--text-secondary)]">{p.runs}</td><td className="hidden md:table-cell py-2 px-3 text-right tabular-nums text-[var(--text-secondary)]">{p.wins}</td><td className="py-2 px-2 sm:px-3 text-right tabular-nums text-[var(--text-secondary)]" title={t("{wins} wins in {runs} runs", {
                wins: p.wins,
                runs: p.runs
              })}>{p.win_rate.toFixed(1)}%</td><td className="py-2 px-2 sm:px-3">{p.main_character && <span title={ladderTitle(p, name)}><CharacterTag id={p.main_character} name={name(p.main_character)} showName={false} size={20} /></span>}</td></tr>;
          })}</tbody></table>}</div>;
    $[58] = board;
    $[59] = failed;
    $[60] = name;
    $[61] = rows_0;
    $[62] = t;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== t10 || $[65] !== t13 || $[66] !== t27 || $[67] !== t28) {
    t29 = <div className="mx-auto max-w-[1100px] px-3 sm:px-5 py-6">{t10}{t13}{t27}{t28}</div>;
    $[64] = t10;
    $[65] = t13;
    $[66] = t27;
    $[67] = t28;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  return t29;
}
function _temp2(n) {
  return n + 1;
}
function _temp() {}